import {
    Component,
    ElementRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NewsletterService } from './footer-subscribe.service';
import { ToastrService } from 'ngx-toastr';
import { GtmService } from 'app/services/gtm.service';

@Component({
    selector: 'app-footer-subscribe',
    standalone: true,
    imports: [TranslateModule, CommonModule, FormsModule],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './footer-subscribe.component.html',
    styleUrl: './footer-subscribe.component.scss',
})
export class FooterSubscribeComponent {
    email: string = '';
    errorMessage: string = '';
    isSubmitting: boolean = false;

    @ViewChild('gaTrigger') gaTrigger!: ElementRef<HTMLButtonElement>;

    gaEventId: string = '';
    gaEventCategory: string = '';
    gaEventLabel: string = '';

    constructor(
        private newsletterService: NewsletterService,
        private toastr: ToastrService,
        private translate: TranslateService,
        private gtmService: GtmService
    ) {}

    fireGA(eventId: string, category: string, label: string) {
        this.gaEventId = eventId;
        this.gaEventCategory = category;
        this.gaEventLabel = label;

        setTimeout(() => {
            if (this.gaTrigger) {
                this.gaTrigger.nativeElement.click();
            }
        }, 0);
    }

    validateEmail(): boolean {
        this.errorMessage = '';

        if (this.email.length < 5 || this.email.length > 254) {
            this.errorMessage = 'FOOTER.NEWSLETTER_EMAIL_VALIDATION';
            return false;
        }

        const atCount = (this.email.match(/@/g) || []).length;
        if (atCount !== 1) {
            this.errorMessage = 'FOOTER.NEWSLETTER_EMAIL_VALIDATION';
            return false;
        }

        const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const allowedCharsRegex = /^[a-zA-Z0-9._+-@]+$/;

        if (
            !emailRegex.test(this.email) ||
            !allowedCharsRegex.test(this.email)
        ) {
            this.errorMessage = 'FOOTER.NEWSLETTER_EMAIL_VALIDATION';
            return false;
        }

        return true;
    }

    // Email input click
    onEmailFocus(): void {
        this.fireGA(
            'footer_email_click',
            'Footer Subscription',
            'Email Input Focus'
        );
    }

    onEmailInput(): void {
        if (this.email.trim()) {
            this.validateEmail();
        } else {
            this.errorMessage = '';
        }
    }

    onSubmit(): void {
        this.fireGA(
            'footer_join_click',
            'Footer Subscription',
            'Join Now Click'
        );

        if (!this.email.trim()) {
            this.errorMessage = 'FOOTER.NEWSLETTER_EMAIL_VALIDATION';
            this.fireGA(
                'footer_invalid_email',
                'Footer Subscription',
                'Invalid Email'
            );
            return;
        }

        if (!this.validateEmail()) {
            this.fireGA(
                'footer_invalid_email',
                'Footer Subscription',
                'Invalid Email'
            );
            return;
        }

        if (this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        const payload = { subscriberEmail: this.email.trim() };

        this.newsletterService.submitNewsletter(payload).subscribe({
            next: (response) => {
                this.isSubmitting = false;

                if (response.code === 200) {
                    const toastRef = this.toastr.success(
                        this.translate.instant(
                            'FOOTER.NEWSLETTER_SUCCESS_MESSAGE'
                        ),
                        '',
                        { timeOut: 5000 }
                    );

                    this.fireGA(
                        'footer_subscribe_success',
                        'Footer Subscription',
                        'Subscription Success'
                    );

                    toastRef.onHidden.subscribe(() => {
                        this.fireGA(
                            'footer_toast_autoclose',
                            'Footer Subscription',
                            'Toast Auto Close'
                        );
                    });

                    toastRef.onTap.subscribe(() => {
                        this.fireGA(
                            'footer_toast_manual_close',
                            'Footer Subscription',
                            'Toast Manual Close'
                        );
                    });

                    this.email = '';
                    this.errorMessage = '';
                } else if (response.code === 400) {
                    const validationErrors = response.data?.subscriberEmail;

                    if (
                        validationErrors &&
                        validationErrors.some(
                            (err: string) =>
                                err.toLowerCase().includes('already') ||
                                err.toLowerCase().includes('taken')
                        )
                    ) {
                        this.toastr.error(
                            this.translate.instant(
                                'FOOTER.NEWSLETTER_EMAIL_DUPLICATE'
                            )
                        );
                        this.fireGA(
                            'footer_duplicate_email',
                            'Footer Subscription',
                            'Duplicate Email'
                        );
                    } else {
                        this.toastr.error(
                            this.translate.instant(
                                'FOOTER.NEWSLETTER_EMAIL_VALIDATION'
                            )
                        );
                        this.fireGA(
                            'footer_invalid_email',
                            'Footer Subscription',
                            'Invalid Email'
                        );
                    }
                }
            },
            error: (error) => {
                this.isSubmitting = false;

                if (error.status === 400) {
                    const validationErrors = error.error?.data?.subscriberEmail;

                    if (
                        validationErrors &&
                        validationErrors.some(
                            (err: string) =>
                                err.toLowerCase().includes('already') ||
                                err.toLowerCase().includes('taken')
                        )
                    ) {
                        this.toastr.error(
                            this.translate.instant(
                                'FOOTER.NEWSLETTER_EMAIL_DUPLICATE'
                            )
                        );
                        this.fireGA(
                            'footer_duplicate_email',
                            'Footer Subscription',
                            'Duplicate Email'
                        );
                    } else {
                        this.toastr.error(
                            this.translate.instant(
                                'FOOTER.NEWSLETTER_EMAIL_VALIDATION'
                            )
                        );
                        this.fireGA(
                            'footer_invalid_email',
                            'Footer Subscription',
                            'Invalid Email'
                        );
                    }
                }
            },
        });
    }
}
